import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';

// ใช้ axios โดยตรงแทน api service ที่มีการเพิ่ม token
const api = axios.create({
  // baseURL: 'http://localhost:5000/api',
  baseURL: 'https://ssk3ita.backend.kruwirat.com/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// ฟังก์ชันช่วยเหลือสำหรับตรวจสอบเลขบัตรประชาชน
const isAllSameDigits = (idCard) => {
  const cleanIdCard = idCard.replace(/-/g, '');
  return cleanIdCard.split('').every(digit => digit === cleanIdCard[0]);
};

const isSequentialDigits = (idCard) => {
  const cleanIdCard = idCard.replace(/-/g, '');
  const digits = cleanIdCard.split('').map(Number);

  const checkSequential = (start, length) => {
    const subDigits = digits.slice(start, start + length);
    const isAscending = subDigits.every((digit, index) => {
      if (index === 0) return true;
      return digit === (subDigits[0] + index) % 10;
    });
    const isDescending = subDigits.every((digit, index) => {
      if (index === 0) return true;
      return digit === (subDigits[0] - index + 10) % 10;
    });
    return isAscending || isDescending;
  };

  return checkSequential(0, 8);
};

const QuestionnairePage = () => {
  const { type, fiscalYear, schoolId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [school, setSchool] = useState(null);
  const [activeFiscalYear, setActiveFiscalYear] = useState(null);
  const [questionnaireName, setQuestionnaireName] = useState('');
  const [responses, setResponses] = useState({});
  const [respondentInfo, setRespondentInfo] = useState({
    id_card: '',
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitPeriod, setIsSubmitPeriod] = useState(true);
  const [settings, setSettings] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  // เพิ่ม state สำหรับโควตา
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [hasQuota, setHasQuota] = useState(false);
  const [quotaLoading, setQuotaLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // ดึงข้อมูลปีงบประมาณ
        const fiscalYearRes = await api.get(`/fiscal-years/${fiscalYear}`);
        setActiveFiscalYear(fiscalYearRes.data);

        // ดึงข้อมูลโรงเรียน
        const schoolRes = await api.get(`/schools/${schoolId}`);
        setSchool(schoolRes.data);

        // ดึงข้อมูลการตั้งค่าระบบ
        const settingsRes = await api.get(`/system-settings/${fiscalYear}`);
        setSettings(settingsRes.data);

        // ตรวจสอบโควตาผู้ตอบแบบสอบถาม
        try {
          const quotaRes = await api.get(`/respondents/count/${schoolId}/${fiscalYear}`);
          setQuotaInfo(quotaRes.data);
          
          // ตรวจสอบโควตาตามประเภทแบบสอบถาม
          let currentQuota = 0;
          if (type === '1') { // IIT
            currentQuota = quotaRes.data.iit.quota;
          } else if (type === '2') { // EIT1
            currentQuota = quotaRes.data.eit1.quota;
          } else if (type === '3') { // EIT2
            currentQuota = quotaRes.data.eit2.quota;
          }
          
          setHasQuota(currentQuota > 0);
        } catch (quotaError) {
          console.error('Error fetching quota:', quotaError);
          setHasQuota(false);
        } finally {
          setQuotaLoading(false);
        }

        // ตรวจสอบช่วงเวลาการตอบแบบสอบถาม
        const now = new Date();
        let startDate, endDate;

        if (type === '1') {
          startDate = new Date(settingsRes.data.iit_start_date);
          endDate = new Date(settingsRes.data.iit_end_date);
          setQuestionnaireName('แบบสอบถามความคิดเห็นผู้มีส่วนได้ส่วนเสียภายใน (IIT)');
        } else if (type === '2') {
          startDate = new Date(settingsRes.data.eit1_start_date);
          endDate = new Date(settingsRes.data.eit1_end_date);
          setQuestionnaireName('แบบสอบถามความคิดเห็นผู้มีส่วนได้ส่วนเสียภายนอก (EIT1 - โรงเรียนกำหนด)');
        } else if (type === '3') {
          startDate = new Date(settingsRes.data.eit2_start_date);
          endDate = new Date(settingsRes.data.eit2_end_date);
          setQuestionnaireName('แบบสอบถามความคิดเห็นผู้มีส่วนได้ส่วนเสียภายนอก (EIT2 - ทั่วไป)');
        }

        setIsSubmitPeriod(now >= startDate && now <= endDate);

        // ดึงข้อมูลคำถาม
        const questionsRes = await api.get(`/questionnaire/${type}/${fiscalYear}`);
        setQuestions(questionsRes.data);

        // สร้าง responses เริ่มต้น
        const initialResponses = {};
        questionsRes.data.forEach(question => {
          initialResponses[question.id] = null;
        });
        setResponses(initialResponses);

      } catch (error) {
        console.error('Error fetching data:', error);
        setError('ไม่สามารถโหลดข้อมูลแบบสอบถามได้ กรุณาลองใหม่ภายหลัง');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [type, fiscalYear, schoolId]);

  const handleRespondentInfoChange = (e) => {
    const { name, value } = e.target;
    setRespondentInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleResponseChange = (questionId, optionId) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: optionId
    }));
  };

  const validateResponses = () => {
    // ตรวจสอบข้อมูลผู้ตอบแบบสอบถาม
    if (!respondentInfo.id_card) {
      Swal.fire({
        icon: 'warning',
        title: 'ข้อมูลไม่ครบถ้วน',
        text: 'กรุณากรอกเลขประจำตัวประชาชน',
        confirmButtonText: 'ตกลง'
      });
      return false;
    }

    // ตรวจสอบเลขบัตรประชาชน
    const cleanIdCard = respondentInfo.id_card.replace(/-/g, '');
    if (!/^\d{13}$/.test(cleanIdCard)) {
      Swal.fire({
        icon: 'warning',
        title: 'ข้อมูลไม่ถูกต้อง',
        text: 'กรุณากรอกเลขประจำตัวประชาชน 13 หลัก',
        confirmButtonText: 'ตกลง'
      });
      return false;
    }

    // ตรวจสอบว่าเลขบัตรประชาชนไม่ใช่เลขซ้ำกันทั้งหมด
    if (isAllSameDigits(cleanIdCard)) {
      Swal.fire({
        icon: 'warning',
        title: 'ข้อมูลไม่ถูกต้อง',
        text: 'เลขประจำตัวประชาชนไม่สามารถเป็นเลขเดียวกันทั้งหมดได้',
        confirmButtonText: 'ตกลง'
      });
      return false;
    }

    // ตรวจสอบว่าเลขบัตรประชาชนไม่ใช่เลขเรียงกันบางส่วน
    if (isSequentialDigits(cleanIdCard)) {
      Swal.fire({
        icon: 'warning',
        title: 'ข้อมูลไม่ถูกต้อง',
        text: 'เลขประจำตัวประชาชนไม่สามารถเป็นเลขเรียงกันได้',
        confirmButtonText: 'ตกลง'
      });
      return false;
    }

    // ตรวจสอบชื่อ-นามสกุลและเบอร์โทรศัพท์เฉพาะเมื่อไม่ใช่ EIT2
    if (type !== '3') {
      if (!respondentInfo.name) {
        Swal.fire({
          icon: 'warning',
          title: 'ข้อมูลไม่ครบถ้วน',
          text: 'กรุณากรอกชื่อ-นามสกุล',
          confirmButtonText: 'ตกลง'
        });
        return false;
      }

      if (!respondentInfo.phone) {
        Swal.fire({
          icon: 'warning',
          title: 'ข้อมูลไม่ครบถ้วน',
          text: 'กรุณากรอกเบอร์โทรศัพท์',
          confirmButtonText: 'ตกลง'
        });
        return false;
      }

      if (!/^\d{10}$/.test(respondentInfo.phone.replace(/-/g, ''))) {
        Swal.fire({
          icon: 'warning',
          title: 'ข้อมูลไม่ถูกต้อง',
          text: 'กรุณากรอกเบอร์โทรศัพท์ให้ถูกต้อง (10 หลัก)',
          confirmButtonText: 'ตกลง'
        });
        return false;
      }
    }

    // ตรวจสอบว่าตอบครบทุกข้อหรือไม่
    for (const questionId in responses) {
      if (responses[questionId] === null) {
        Swal.fire({
          icon: 'warning',
          title: 'กรุณาตอบให้ครบทุกข้อ',
          text: 'คุณยังไม่ได้ตอบคำถามบางข้อ กรุณาตรวจสอบอีกครั้ง',
          confirmButtonText: 'ตกลง'
        });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateResponses()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const responseList = [];
      for (const questionId in responses) {
        responseList.push({
          question_id: parseInt(questionId),
          option_id: responses[questionId]
        });
      }

      await api.post('/responses', {
        school_id: parseInt(schoolId),
        fiscal_year_id: parseInt(fiscalYear),
        questionnaire_type_id: parseInt(type),
        id_card: respondentInfo.id_card.replace(/-/g, ''),
        name: respondentInfo.name,
        email: respondentInfo.email,
        phone: respondentInfo.phone.replace(/-/g, ''),
        responses: responseList
      });

      Swal.fire({
        icon: 'success',
        title: 'ส่งแบบสอบถามสำเร็จ',
        text: 'ขอบคุณสำหรับการตอบแบบสอบถาม',
        confirmButtonText: 'ตกลง'
      });

      setSubmitted(true);

    } catch (error) {
      console.error('Error submitting responses:', error);

      if (error.response && error.response.status === 400 && error.response.data.message.includes('ได้ตอบแบบสอบถามนี้แล้ว')) {
        Swal.fire({
          icon: 'error',
          title: 'ไม่สามารถส่งแบบสอบถามได้',
          text: 'บุคคลนี้ได้ตอบแบบสอบถามนี้แล้ว',
          confirmButtonText: 'ตกลง'
        });
      } else if (error.response && error.response.status === 400 && error.response.data.message.includes('ครบตามโควตา')) {
        Swal.fire({
          icon: 'error',
          title: 'ไม่สามารถส่งแบบสอบถามได้',
          text: 'จำนวนผู้ตอบแบบสอบถามครบตามโควตาแล้ว',
          confirmButtonText: 'ตกลง'
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถส่งแบบสอบถามได้ กรุณาลองใหม่ภายหลัง',
          confirmButtonText: 'ตกลง'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || quotaLoading) {
    return (
      <div className="container py-5">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">กำลังโหลด...</span>
          </div>
          <p className="mt-3">กำลังโหลดข้อมูลและตรวจสอบสิทธิ์การเข้าถึง...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="card shadow">
          <div className="card-header bg-danger text-white">
            <h3 className="mb-0">เกิดข้อผิดพลาด</h3>
          </div>
          <div className="card-body p-4">
            <p>{error}</p>
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              <i className="fas fa-sync-alt me-2"></i> ลองใหม่
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ตรวจสอบว่ามีการกำหนดโควตาหรือไม่
  if (!hasQuota) {
    const getQuotaTypeName = () => {
      if (type === '1') return 'IIT (บุคลากรภายในโรงเรียน)';
      if (type === '2') return 'EIT1 (บุคคลที่โรงเรียนกำหนด)';
      if (type === '3') return 'EIT2 (บุคคลภายนอกทั่วไป)';
      return 'ไม่ทราบประเภท';
    };

    return (
      <div className="container py-5">
        <div className="card shadow">
          <div className="card-header bg-warning text-dark">
            <div className="d-flex align-items-center">
              <i className="fas fa-exclamation-triangle text-light fa-2x me-3"></i>
              <div>
                <h3 className="mb-0 text-light">ยังไม่มีการกำหนดจำนวนผู้ตอบแบบสอบถาม</h3>
                <small className=" text-light">ไม่สามารถเข้าถึงแบบสอบถามได้ในขณะนี้</small>
              </div>
            </div>
          </div>
          <div className="card-body p-4">
            <div className="alert alert-warning mb-4">
              <div className="row">
                <div className="col-md-2 text-center">
                  <i className="fas fa-users fa-4x text-warning"></i>
                </div>
                <div className="col-md-10">
                  <h5 className="alert-heading mb-3">
                    <i className="fas fa-info-circle me-2"></i>
                    ข้อมูลแบบสอบถาม
                  </h5>
                  <div className="row mb-3">
                    <div className="col-sm-4"><strong>โรงเรียน:</strong></div>
                    <div className="col-sm-8">{school?.name}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-4"><strong>ปีงบประมาณ:</strong></div>
                    <div className="col-sm-8">{activeFiscalYear?.year}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-4"><strong>ประเภท:</strong></div>
                    <div className="col-sm-8">{getQuotaTypeName()}</div>
                  </div>
                  <div className="row">
                    <div className="col-sm-4"><strong>สถานะ:</strong></div>
                    <div className="col-sm-8">
                      <span className="badge bg-danger">ยังไม่กำหนดจำนวนผู้ตอบ</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="alert alert-info">
              <h5 className="alert-heading">
                <i className="fas fa-lightbulb me-2"></i>
                เหตุผลที่ไม่สามารถเข้าถึงแบบสอบถามได้
              </h5>
              <ul className="mb-0">
                <li>โรงเรียนยังไม่ได้กำหนดจำนวนผู้ตอบแบบสอบถามสำหรับประเภทนี้</li>
                <li>จำนวนผู้ตอบที่กำหนดเป็น 0 คน หรือยังไม่มีการตั้งค่า</li>
                <li>ระบบต้องการให้มีการกำหนดจำนวนผู้ตอบล่วงหน้าเพื่อควบคุมการตอบแบบสอบถาม</li>
              </ul>
            </div>

            <div className="card">
              <div className="card-header bg-light">
                <h5 className="mb-0">
                  <i className="fas fa-phone me-2"></i>
                  ขั้นตอนการแก้ไขปัญหา
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="text-primary">
                      <i className="fas fa-user-tie me-2"></i>
                      สำหรับผู้ดูแลระบบโรงเรียน
                    </h6>
                    <ol>
                      <li>เข้าสู่ระบบด้วยบัญชีผู้ดูแลโรงเรียน</li>
                      <li>ไปที่เมนู "จัดการผู้ตอบแบบสอบถาม"</li>
                      <li>กำหนดจำนวนผู้ตอบแบบสอบถามแต่ละประเภท</li>
                      <li>บันทึกการตั้งค่า</li>
                    </ol>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-success">
                      <i className="fas fa-headset me-2"></i>
                      ติดต่อเพื่อขอความช่วยเหลือ
                    </h6>
                    <ul className="list-unstyled">
                      <li><i className="fas fa-phone me-2 text-primary"></i> 045-611-405</li>
                      <li><i className="fas fa-envelope me-2 text-primary"></i> ssk3@obec.go.th</li>
                      <li><i className="fas fa-globe me-2 text-primary"></i> www.ssk3.go.th</li>
                      <li><i className="fas fa-clock me-2 text-primary"></i> วันจันทร์-ศุกร์ เวลา 08:30-16:30 น.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-4">
              <button
                className="btn btn-primary me-2"
                onClick={() => window.location.reload()}
              >
                <i className="fas fa-sync-alt me-2"></i> ตรวจสอบอีกครั้ง
              </button>
              <a href="/" className="btn btn-secondary">
                <i className="fas fa-home me-2"></i> กลับสู่หน้าหลัก
              </a>
            </div>
          </div>
          <div className="card-footer text-center bg-light">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              หากคุณเป็นผู้ดูแลระบบโรงเรียน กรุณาเข้าสู่ระบบเพื่อกำหนดจำนวนผู้ตอบแบบสอบถาม | 
              © {new Date().getFullYear()} สำนักงานเขตพื้นที่การศึกษาประถมศึกษาศรีสะเกษ เขต 3
            </small>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="container py-5">
        <div className="card shadow">
          <div className="card-body text-center p-5">
            <div className="mb-4">
              <i className="fas fa-check-circle text-success" style={{ fontSize: '5rem' }}></i>
            </div>
            <h2 className="mb-4">ขอบคุณสำหรับการตอบแบบสอบถาม</h2>
            <p className="mb-4">คำตอบของท่านถูกบันทึกเรียบร้อยแล้ว</p>
            <a href="/" className="btn btn-primary">กลับสู่หน้าหลัก</a>
          </div>
        </div>
      </div>
    );
  }

  if (!isSubmitPeriod) {
    return (
      <div className="container py-5">
        <div className="card shadow">
          <div className="card-header bg-danger text-white">
            <h3 className="mb-0">ไม่อยู่ในช่วงเวลาการตอบแบบสอบถาม</h3>
          </div>
          <div className="card-body p-4">
            <p>ขณะนี้ไม่อยู่ในช่วงเวลาการตอบแบบสอบถาม กรุณาติดต่อผู้ดูแลระบบ</p>
            {settings && (
              <div className="alert alert-info">
                <h5 className="alert-heading">ช่วงเวลาการตอบแบบสอบถาม</h5>
                {type === '1' && (
                  <p>
                    {new Date(settings.iit_start_date).toLocaleString('th-TH')} ถึง {new Date(settings.iit_end_date).toLocaleString('th-TH')}
                  </p>
                )}
                {type === '2' && (
                  <p>
                    {new Date(settings.eit1_start_date).toLocaleString('th-TH')} ถึง {new Date(settings.eit1_end_date).toLocaleString('th-TH')}
                  </p>
                )}
                {type === '3' && (
                  <p>
                    {new Date(settings.eit2_start_date).toLocaleString('th-TH')} ถึง {new Date(settings.eit2_end_date).toLocaleString('th-TH')}
                  </p>
                )}
              </div>
            )}
            <a href="/" className="btn btn-primary mt-3">กลับสู่หน้าหลัก</a>
          </div>
        </div>
      </div>
    );
  }

  const groupedQuestions = {};
  questions.forEach(q => {
    if (!groupedQuestions[q.topic_name]) {
      groupedQuestions[q.topic_name] = [];
    }
    groupedQuestions[q.topic_name].push(q);
  });

  return (
    <div className="container py-4">
      <div className="card shadow mb-4">
        <div className="card-header bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0">{questionnaireName}</h2>
            <div className="text-end">
              <small>ปีงบประมาณ {activeFiscalYear?.year}</small>
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="mb-4">
            <div className="row">
              <div className="col-md-6">
                <h4 className="mb-3">ข้อมูลแบบสอบถาม</h4>
                <p><strong>โรงเรียน:</strong> {school?.name}</p>
                <p><strong>ปีงบประมาณ:</strong> {activeFiscalYear?.year}</p>
                <p><strong>ประเภทแบบสอบถาม:</strong> {questionnaireName}</p>
                <p><strong>จำนวนคำถาม:</strong> {questions.length} ข้อ</p>
                
                {/* แสดงข้อมูลโควตา */}
                {quotaInfo && (
                  <div className="alert alert-success mt-3">
                    <h6 className="alert-heading">ข้อมูลผู้ตอบแบบสอบถาม</h6>
                    {type === '1' && (
                      <p className="mb-0">ตอบแล้ว: {quotaInfo.iit.current}/{quotaInfo.iit.quota} คน</p>
                    )}
                    {type === '2' && (
                      <p className="mb-0">ตอบแล้ว: {quotaInfo.eit1.current}/{quotaInfo.eit1.quota} คน</p>
                    )}
                    {type === '3' && (
                      <p className="mb-0">ตอบแล้ว: {quotaInfo.eit2.current}/{quotaInfo.eit2.quota} คน</p>
                    )}
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <div className="alert alert-info">
                  <h5 className="alert-heading">คำชี้แจง</h5>
                  <p className="mb-0">แบบสอบถามนี้จัดทำขึ้นเพื่อการประเมินคุณธรรมและความโปร่งใสในการดำเนินงานของหน่วยงานภาครัฐ (ITA) ประจำปีงบประมาณ {activeFiscalYear?.year} โปรดตอบตามความเป็นจริง เพื่อนำข้อมูลไปพัฒนาการดำเนินงานของโรงเรียนต่อไป</p>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="card mb-4">
              <div className="card-header bg-info text-white">
                <h4 className="mb-0">ข้อมูลผู้ตอบแบบสอบถาม</h4>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="id_card" className="form-label">เลขประจำตัวประชาชน <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      id="id_card"
                      name="id_card"
                      value={respondentInfo.id_card}
                      onChange={handleRespondentInfoChange}
                      placeholder="เลขประจำตัวประชาชน 13 หลัก"
                      required
                      maxLength="13"
                      minLength="13"
                    />
                    <div className="form-text">กรอกเลขประจำตัวประชาชน 13 หลัก ไม่ต้องใส่ขีด</div>
                  </div>
                  {type !== '3' && (
                    <>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="name" className="form-label">ชื่อ-นามสกุล <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          id="name"
                          name="name"
                          value={respondentInfo.name}
                          onChange={handleRespondentInfoChange}
                          placeholder="ชื่อ-นามสกุล"
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="email" className="form-label">อีเมล</label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          name="email"
                          value={respondentInfo.email}
                          onChange={handleRespondentInfoChange}
                          placeholder="อีเมล (ถ้ามี)"
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="phone" className="form-label">เบอร์โทรศัพท์ <span className="text-danger">*</span></label>
                        <input
                          type="tel"
                          className="form-control"
                          id="phone"
                          name="phone"
                          value={respondentInfo.phone}
                          onChange={handleRespondentInfoChange}
                          placeholder="เบอร์โทรศัพท์"
                          required
                        />
                        <div className="form-text">กรอกเบอร์โทรศัพท์ 10 หลัก ไม่ต้องใส่ขีด</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {Object.keys(groupedQuestions).map((topicName, topicIndex) => (
              <div key={topicIndex} className="card mb-4">
                <div className="card-header bg-secondary text-white">
                  <h4 className="mb-0">{topicName}</h4>
                </div>
                <div className="card-body">
                  {groupedQuestions[topicName].map((question, qIndex) => (
                    <div key={question.id} className="mb-4 pb-4 border-bottom">
                      <h5 className="mb-3">
                        ข้อ {qIndex + 1} {question.question_text}
                      </h5>
                      <div className="ms-3">
                        {question.options?.map(option => (
                          <div key={option.id} className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name={`question_${question.id}`}
                              id={`option_${option.id}`}
                              value={option.id}
                              checked={responses[question.id] === option.id}
                              onChange={() => handleResponseChange(question.id, option.id)}
                              required
                            />
                            <label className="form-check-label" htmlFor={`option_${option.id}`}>
                              {option.option_text}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="d-grid gap-2 col-md-6 mx-auto">
              <button
                type="submit"
                className="btn btn-primary btn-lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    กำลังส่งแบบสอบถาม...
                  </>
                ) : 'ส่งแบบสอบถาม'}
              </button>
            </div>
          </form>
        </div>
        <div className="card-footer text-center">
          <small className="text-muted">© {new Date().getFullYear()} สำนักงานเขตพื้นที่การศึกษาประถมศึกษาศรีสะเกษ เขต 3</small>
        </div>
      </div>
    </div>
  );
};

export default QuestionnairePage;